import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plane, Plus, Check, Ban, CalendarOff, RefreshCw, AlertTriangle, Pencil, RotateCcw } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { isoDate } from '../../utils/time';
import {
  PageHead, Register, FigureStrip, Figure, DetailDrawer, AcStatus, Field, FormRow, fmtDate,
} from './design';

const INP = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// Inclusive day count between two ISO/parseable dates. Returns 0 when either is missing/invalid.
const dayCount = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(from);
  const b = new Date(to);
  if (isNaN(a) || isNaN(b)) return 0;
  const ms = b.setHours(0, 0, 0, 0) - a.setHours(0, 0, 0, 0);
  if (ms < 0) return 0;
  return Math.floor(ms / 86400000) + 1;
};

const ymd = (v) => String(v || '').slice(0, 10);
// The Australian financial year (1 July to 30 June) that holds an ISO day.
const financialYear = (day) => {
  const y = Number(day.slice(0, 4));
  const start = Number(day.slice(5, 7)) >= 7 ? y : y - 1;
  return { from: `${start}-07-01`, to: `${start + 1}-06-30` };
};
const visits = (n) => `${n} visit${n === 1 ? '' : 's'}`;

const SCHEME_NAME = { SAH: 'Support at Home', HCP: 'Home Care Package' };
const schemeName = (s) => (s ? (SCHEME_NAME[String(s).toUpperCase()] || s) : '');
const typeLabel = (type) => (type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Leave');

const LEAVE_TYPES = ['Social', 'Hospital', 'Respite', 'Transition'];

const emptyForm = {
  client_id: '',
  type: 'Social',
  from_date: '',
  to_date: '',
  reason: '',
  description: '',
  internal_comments: '',
  status: 'Awaiting Approval',
};

const isAwaiting = status => {
  const s = (status || '').toLowerCase();
  return s === 'awaiting approval' || s === 'pending';
};
const isApproved = (status) => (status || '').toLowerCase() === 'approved';
// Rejected and withdrawn leave is finished with, but either can be put back to awaiting approval.
const isClosed = (status) => ['rejected', 'withdrawn'].includes((status || '').toLowerCase());

// The register stores 'Awaiting Approval' and 'pending' for the same fact; both read the same way.
// Withdrawn is grey, not red: the leave was approved and then taken back, and nothing is wrong.
const LeaveStatus = ({ status }) => {
  const s = (status || '').toLowerCase();
  if (isAwaiting(s)) return <AcStatus status="pending" label="Awaiting approval" />;
  if (s === 'approved') return <AcStatus status="approved" />;
  if (s === 'rejected') return <AcStatus status="rejected" />;
  if (s === 'withdrawn') return <AcStatus status="withdrawn" label="Withdrawn" />;
  return <AcStatus status={status || 'draft'} label={status || 'Draft'} />;
};

export default function AgedCareLeave({ clientId: only = '', embedded = false } = {}) {
  const [loading, setLoading] = useState(true);
  const [allLeave, setLeave] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const leave = only ? allLeave.filter((r) => String(r.client_id) === String(only)) : allLeave;
  const [clients, setClients] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // the leave being corrected; null when adding
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState(''); // row id currently being approved/rejected
  const confirm = useConfirm();

  const loadLeave = async () => {
    setLoading(true); setLoadError(null);
    try {
      const res = await api.get('/aged-care/leave');
      const rows = Array.isArray(res.data) ? res.data : res.data?.leave || [];
      setLeave(rows);
    } catch (e) {
      setLeave([]);
      setLoadError(e.response?.data?.message || 'Could not load the leave register');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await api.get('/aged-care/clients');
      const rows = Array.isArray(res.data) ? res.data : res.data?.clients || [];
      setClients(rows);
    } catch (e) {
      setClients([]);
    }
  };

  useEffect(() => {
    loadLeave();
    loadClients();
  }, []);

  const nameOf = (c) => c.name || c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
  // A participant with no name on record reads as 'Participant', never as an id.
  const nameForId = (id) => {
    const c = clients.find((x) => String(x.id) === String(id));
    if (c && nameOf(c)) return nameOf(c);
    const l = allLeave.find((x) => String(x.client_id) === String(id) && x.client_name);
    return l ? l.client_name : 'Participant';
  };
  const clientName = row => {
    if (row.client_name) return row.client_name;
    if (row.client && (row.client.name || row.client.full_name)) return row.client.name || row.client.full_name;
    return nameForId(row.client_id);
  };

  const openAdd = () => {
    // Inside a participant's file the leave is theirs: there is no one else to choose.
    setEditing(null);
    setForm({ ...emptyForm, client_id: only || '' });
    setShowModal(true);
  };
  const openEdit = (row) => {
    // Status is not in the form: approving and withdrawing stay on the register, where each is
    // confirmed because it changes the roster.
    setEditing(row);
    setForm({
      ...emptyForm,
      client_id: row.client_id || '',
      type: row.type ? typeLabel(row.type) : 'Social',
      from_date: ymd(row.from_date),
      to_date: ymd(row.to_date),
      reason: row.reason || '',
      description: row.description || '',
      internal_comments: row.internal_comments || '',
      status: row.status || 'Awaiting Approval',
    });
    setShowModal(true);
  };
  // Stable, or the drawer re-runs its focus effect on every keystroke inside the form.
  const closeAdd = useCallback(() => setShowModal(false), []);

  const setType = type => {
    // Reason mirrors the type by default (clinician can still override before save).
    setForm(f => ({ ...f, type, reason: f.reason && !LEAVE_TYPES.includes(f.reason) ? f.reason : type }));
  };

  const submit = async () => {
    if (!editing && !form.client_id) {
      toast.error('Select a participant');
      return;
    }
    if (!form.from_date || !form.to_date) {
      toast.error('Enter a from and to date');
      return;
    }
    if (dayCount(form.from_date, form.to_date) === 0) {
      toast.error('The "to" date must be on or after the "from" date');
      return;
    }
    const days = dayCount(form.from_date, form.to_date);
    setSaving(true);
    try {
      if (editing) {
        // Changing an approved leave puts its visits back, then takes out the visits in the new
        // window; the server says how many of each, and the toast repeats it.
        const { data } = await api.patch(`/aged-care/leave/${editing.id}`, {
          type: form.type,
          from_date: form.from_date,
          to_date: form.to_date,
          days,
          reason: form.reason || form.type,
          description: form.description,
          internal_comments: form.internal_comments,
        });
        const back = data?.restored_shifts || 0;
        const out = data?.cancelled_shifts || 0;
        let msg = 'Saved';
        if (back && out) msg = `Saved. ${visits(back)} put back, ${out} taken out`;
        else if (back) msg = `Saved. ${visits(back)} put back`;
        else if (out) msg = `Saved. ${visits(out)} taken out`;
        toast.success(msg);
      } else {
        await api.post('/aged-care/leave', {
          ...form,
          reason: form.reason || form.type,
          days,
        });
        toast.success('Leave recorded');
      }
      setShowModal(false);
      setForm(emptyForm);
      loadLeave();
    } catch (e) {
      // The create route puts its full sentence in `error` (a pathway clash names the pathway);
      // the update route puts it in `message`.
      const d = e?.response?.data || {};
      toast.error((editing ? (d.message || d.error) : (d.error || d.message)) || (editing ? 'Could not save the changes' : 'Could not save leave'));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (row, status) => {
    // Approving takes the participant out of the visits rostered inside the window, so it is
    // confirmed first. So is withdrawing an approval, which puts those visits back. Rejecting a
    // request, or putting one back to awaiting approval, leaves the roster alone.
    const withdrawing = status !== 'Approved' && isApproved(row.status);
    if (status === 'Approved') {
      const days = daysOf(row);
      const ok = await confirm(
        `Visits rostered for ${clientName(row)} from ${fmtDate(row.from_date)} to ${fmtDate(row.to_date)} (${days} day${days === 1 ? '' : 's'}) will be cancelled, and each worker is told. A group visit goes ahead without ${clientName(row)}. Anything already delivered or invoiced is left alone.`,
        { title: 'Approve this leave?', confirmLabel: 'Approve leave' },
      );
      if (!ok) return;
    } else if (withdrawing) {
      const ok = await confirm(
        `The visits this leave cancelled for ${clientName(row)} are put back on the roster, and each worker is told.`,
        { title: 'Withdraw this leave?', confirmLabel: 'Withdraw leave' },
      );
      if (!ok) return;
    }
    setActingId(row.id);
    try {
      const { data } = await api.patch(`/aged-care/leave/${row.id}`, { status });
      if (status === 'Approved') {
        const n = data?.cancelled_shifts || 0;
        toast.success(n ? `Leave approved. ${visits(n)} taken off the roster` : 'Leave approved. No visits were rostered in the window');
      } else if (withdrawing) {
        const n = data?.restored_shifts || 0;
        toast.success(n ? `Leave withdrawn. ${visits(n)} back on the roster` : 'Leave withdrawn');
      } else if (status === 'Awaiting Approval') {
        toast.success('Put back to awaiting approval');
      } else {
        toast.success('Leave rejected');
      }
      loadLeave();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Could not update leave');
    } finally {
      setActingId('');
    }
  };

  const daysOf = (row) => (row.days != null ? Number(row.days) : dayCount(row.from_date, row.to_date));

  // ----- Summary stats -----
  const totalAwaiting = leave.filter(r => isAwaiting(r.status)).length;
  const approvedRows = leave.filter(r => isApproved(r.status));
  const onLeaveNow = approvedRows.filter(r => {
    const now = new Date().setHours(0, 0, 0, 0);
    const from = r.from_date ? new Date(r.from_date).setHours(0, 0, 0, 0) : null;
    const to = r.to_date ? new Date(r.to_date).setHours(0, 0, 0, 0) : null;
    return from != null && to != null && from <= now && now <= to;
  }).length;
  // "There are no leave arrangements under Support at Home" (program manual v4.4 §12.1): the budget
  // keeps accruing through a break and nothing is reported. The 28-day leave allowance is a legacy
  // Home Care Package rule, so only HCP participants are counted against it.
  // The allowance is 28 days a financial year for EACH participant, so days are counted inside the
  // current financial year (1 July to 30 June) and per participant: a leave that crosses 1 July
  // counts only its days inside the year, and one person's 30 days is over while ten people's
  // 3 days each is not.
  const isHcp = (r) => String(r.scheme || '').toUpperCase() === 'HCP';
  const fy = financialYear(isoDate());
  const fyDaysOf = (r) => {
    const from = ymd(r.from_date); const to = ymd(r.to_date);
    if (!from || !to || to < fy.from || from > fy.to) return 0;
    if (from >= fy.from && to <= fy.to) return daysOf(r);
    return dayCount(from < fy.from ? fy.from : from, to > fy.to ? fy.to : to);
  };
  const hcpDaysBy = new Map();
  approvedRows.filter(isHcp).forEach((r) => {
    const d = fyDaysOf(r);
    if (d) hcpDaysBy.set(String(r.client_id), (hcpDaysBy.get(String(r.client_id)) || 0) + d);
  });
  const approvedDaysFY = [...hcpDaysBy.values()].reduce((a, n) => a + n, 0);
  const overAllowance = [...hcpDaysBy.values()].filter((n) => n > 28).length;
  const hasHcp = leave.some(isHcp);

  const formDays = dayCount(form.from_date, form.to_date);
  // A type saved before the list was fixed still shows as itself rather than as a blank choice.
  const typeOptions = form.type && !LEAVE_TYPES.includes(form.type) ? [...LEAVE_TYPES, form.type] : LEAVE_TYPES;
  const editingApproved = !!editing && isApproved(editing.status);

  const columns = [
    {
      key: 'client', header: 'Participant', sortable: true, sortValue: (r) => clientName(r),
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-900">{clientName(r)}</div>
          {r.scheme && <div className="text-[11px] text-slate-500">{schemeName(r.scheme)}</div>}
        </div>
      ),
    },
    { key: 'type', header: 'Type', width: 110, sortable: true, render: (r) => typeLabel(r.type) },
    { key: 'from_date', header: 'From', sortable: true, defaultDir: 'desc', render: (r) => fmtDate(r.from_date) },
    { key: 'to_date', header: 'To', sortable: true, render: (r) => fmtDate(r.to_date) },
    {
      key: 'days', header: 'Days', align: 'right', sortable: true, sortValue: daysOf,
      render: (r) => {
        const d = daysOf(r);
        return (
          <span className="inline-flex items-center justify-end gap-1.5">
            <span className="tabular-nums">{d}</span>
            {d > 28 && isHcp(r) && <AcStatus status="overdue" label="Over 28" />}
          </span>
        );
      },
    },
    { key: 'status', header: 'Status', width: 150, render: (r) => <LeaveStatus status={r.status} /> },
    {
      // The decision is the reason this register exists, so it stays visible rather than
      // appearing on hover: approving cancels rostered visits.
      key: 'decision', header: 'Decision', align: 'right', width: 230,
      render: (r) => {
        const acting = actingId === r.id;
        if (isApproved(r.status)) {
          return (
            <button type="button" onClick={() => decide(r, 'Withdrawn')} disabled={acting} className="btn-secondary btn-sm"><Ban size={13} /> Withdraw</button>
          );
        }
        if (isClosed(r.status)) {
          return (
            <button type="button" onClick={() => decide(r, 'Awaiting Approval')} disabled={acting} className="btn-secondary btn-sm whitespace-nowrap"><RotateCcw size={13} /> Put back to awaiting approval</button>
          );
        }
        if (!isAwaiting(r.status)) return <span className="text-slate-300">—</span>;
        return (
          <span className="inline-flex items-center justify-end gap-1.5">
            <button type="button" onClick={() => decide(r, 'Approved')} disabled={acting} className="btn-primary btn-sm"><Check size={13} /> Approve</button>
            <button type="button" onClick={() => decide(r, 'Rejected')} disabled={acting} className="btn-secondary btn-sm"><Ban size={13} /> Reject</button>
          </span>
        );
      },
    },
  ];

  return (
    <div>
      {!embedded && <>
      <PageHead icon={Plane}
        title="Participant leave"
        purpose="Hospital stays, respite, holidays and other breaks. Approving one takes the participant out of the visits rostered in that window. Under Support at Home there is no leave allowance: the budget keeps building up and nothing is reported to Services Australia. The 28-day allowance applies only to legacy Home Care Packages."
        actions={<button type="button" onClick={openAdd} className="btn-primary text-sm"><Plus size={16} /> Add leave</button>}
      />

      <FigureStrip>
        <Figure label="Awaiting approval" value={totalAwaiting} tone={totalAwaiting > 0 ? 'amber' : 'slate'} />
        <Figure label="On leave now" value={onLeaveNow} />
        <Figure label="Approved records" value={approvedRows.length} />
        {hasHcp ? (
          <Figure label="Home Care Package leave days this financial year" value={approvedDaysFY}
            tone={overAllowance ? 'red' : 'slate'}
            sub={overAllowance
              ? `${overAllowance} participant${overAllowance === 1 ? ' is' : 's are'} over the 28-day allowance`
              : 'Each participant has 28 days from 1 July'} />
        ) : (
          <Figure label="Support at Home" value="No allowance" sub="Breaks do not reduce the budget" />
        )}
      </FigureStrip>
      </>}

      <Register
        title={embedded ? 'Leave and hospital stays' : 'Leave register'} count={leave.length} countLabel={leave.length === 1 ? 'record' : 'records'}
        columns={columns} rows={leave} loading={loading} error={loadError} onRetry={loadLeave}
        rowTone={(r) => ((daysOf(r) > 28 && isHcp(r)) ? 'rose' : isAwaiting(r.status) ? 'amber' : undefined)}
        rowActions={(r) => (
          <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(r); }} title="Edit" aria-label={`Edit leave for ${clientName(r)}`} className="btn-ghost btn-sm !px-1.5"><Pencil size={14} /></button>
        )}
        actions={embedded
          ? <button type="button" onClick={openAdd} className="btn-secondary btn-sm"><Plus size={13} /> Add leave</button>
          : <button type="button" onClick={loadLeave} className="btn-secondary btn-sm"><RefreshCw size={13} /> Refresh</button>}
        empty={{
          icon: CalendarOff,
          title: 'No leave recorded',
          hint: 'Add a participant’s break to pause their services for the period. Only legacy Home Care Package days count against an allowance.',
          action: <button type="button" onClick={openAdd} className="btn-primary btn-sm"><Plus size={14} /> Add leave</button>,
        }}
        footer="Leave covers both schemes. Under Support at Home there is no leave allowance: the budget keeps building up while the participant is away. Only legacy Home Care Packages count hospital, respite, transition and social leave against an allowance. Restorative Care and End-of-Life Pathways cannot be paused."
      />

      <DetailDrawer
        open={showModal} onClose={closeAdd} icon={Plane} width="md" title={editing ? 'Edit leave' : 'Add leave'}
        footer={<>
          <button type="button" onClick={closeAdd} className="btn-secondary text-sm">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="btn-primary text-sm">
            <Plane size={16} /> {saving ? 'Saving' : editing ? 'Save changes' : 'Save leave'}
          </button>
        </>}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            {editingApproved ? (
              <p>This leave is approved. Saving puts back the visits it took the participant out of, then takes them out of the visits in the new dates. Each worker is told.</p>
            ) : (
              <p>Approving leave takes the participant out of the visits rostered inside the window. Support at Home has no leave allowance. Only for a legacy Home Care Package can more than 28 leave days in a financial year reduce the subsidy.</p>
            )}
          </div>

          {/* The participant is fixed when correcting a leave, or inside their own file. */}
          {editing || only ? (
            <div>
              <div className="text-[13px] font-medium text-slate-700 mb-1">Participant</div>
              <p className="text-sm text-slate-900">{editing ? clientName(editing) : nameForId(only)}</p>
            </div>
          ) : (
            <Field label="Participant" required hint={clients.length === 0 ? 'No participants loaded yet. You can still enter the dates.' : undefined}>
              {(id) => (
                <select id={id} value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} className={INP}>
                  <option value="">Select a participant</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{nameOf(c) || 'Participant'}</option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field label="Leave type">
            {(id) => (
              <select id={id} value={form.type} onChange={e => setType(e.target.value)} className={INP}>
                {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </Field>

          <FormRow cols={2}>
            <Field label="From date" required>
              {(id) => <input id={id} type="date" value={form.from_date} onChange={e => setForm(f => ({ ...f, from_date: e.target.value }))} className={INP} />}
            </Field>
            <Field label="To date" required>
              {(id) => <input id={id} type="date" value={form.to_date} min={form.from_date || undefined} onChange={e => setForm(f => ({ ...f, to_date: e.target.value }))} className={INP} />}
            </Field>
          </FormRow>

          {form.from_date && form.to_date && (
            <p className="text-[12px] text-slate-500">
              Duration{' '}
              <span className={clsx('font-medium tabular-nums', formDays > 28 ? 'text-red-700' : 'text-slate-700')}>
                {formDays} day{formDays === 1 ? '' : 's'}
              </span>
              {formDays > 28 && ', which is more than 28 days. That only matters for a legacy Home Care Package'}
            </p>
          )}

          <Field label="Reason">
            {(id) => <input id={id} type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className={INP} placeholder="Mirrors the leave type unless you change it" />}
          </Field>

          <Field label="Description">
            {(id) => <textarea id={id} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={INP} placeholder="Admitted to Royal North Shore for a hip replacement, expected discharge in 10 days, for example" />}
          </Field>

          <Field label="Internal comments" hint="Not visible to the participant. Coordination notes for the care team.">
            {(id) => <textarea id={id} rows={2} value={form.internal_comments} onChange={e => setForm(f => ({ ...f, internal_comments: e.target.value }))} className={INP} />}
          </Field>

          {!editing && (
            <Field label="Initial status">
              {(id) => (
                <>
                  <select id={id} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={INP}>
                    <option value="Awaiting Approval">Awaiting approval</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                  {form.status === 'Approved' && (
                    <p className="text-[11.5px] text-amber-700 mt-1">Saving as approved takes the participant out of the visits rostered inside the leave window.</p>
                  )}
                </>
              )}
            </Field>
          )}
        </div>
      </DetailDrawer>
    </div>
  );
}
